import { InputFieldMapper } from './InputFieldMapper';
import {
  InputFileDetails,
  InputFileSelectionOptions,
} from '@/app/features/products/common/components/file-selection';
import {
  ErrorsAndWarningReporter,
  ErrorsAndWarningReporterProps,
} from '@/app/features/products/common/components/frontend-processing/ErrorsAndWarningReporter';
import {
  FieldMappings,
  InputFileUploadResult,
  PreviousFieldMappings,
} from '@/app/features/products/common/components/frontend-processing/models';
import { FieldDelimiter, InputLabelsMap } from '@ssar-app/domain';
import { useCallback, useState } from 'react';
import { Descriptions, Spin } from '@ssar-app/uikit';
import { Grid, GridItem, HStack } from '@chakra-ui/react';

export interface InputFileProcessorProps {
  productName: string;
  tmsNumber: string;
  subCode: string;
  inputLabelsMap: InputLabelsMap;
  details: InputFileDetails;
  previousFieldMappings: PreviousFieldMappings[];
  onAfterViewDisplayed?: () => void;
  onNewFileSelected: (
    details: InputFileDetails,
    currentFieldMappings: FieldMappings[],
  ) => void;
  onSuccess: (uploadResult: InputFileUploadResult) => void;
  isRemoteFile: boolean;
  setIsRemoteFile: (arg0: boolean) => void;
  inputFileSelectionOptions: InputFileSelectionOptions;
  oneClickRemoteFileAccess?: boolean;
}
export const InputFileProcessor = (props: InputFileProcessorProps) => {
  const {
    details,
    inputLabelsMap,
    onSuccess,
    onNewFileSelected,
    productName,
    inputFileSelectionOptions,
  } = props;
  const [errorsAndIsSSNMapped, setErrorsAndIsSSNMapped] =
    useState<ErrorsAndWarningReporterProps>({
      isSSNMapped: true,
      errors: [],
      fieldErrors: [],
    });
  const [isLoading, setIsLoading] = useState(false);
  const onFileUploadSuccess = useCallback(
    (
      clientFileName: string,
      experianFileName: string,
      fieldMappings: FieldMappings[],
      hasHeaders?: boolean,
      recordsExpected?: number,
    ): void => {
      onSuccess({
        clientFileName,
        experianFileName,
        fieldMappings,
        recordsExpected,
        hasHeaders,
      });
    },
    [onSuccess],
  );

  return (
    <Spin spinning={isLoading}>
      <Grid templateColumns='repeat(2, 1fr)' gap={4}>
        <GridItem>
          <Descriptions size='middle' bordered column={1}>
            <Descriptions.Item label='Input File Name'>
              {details.inputFile?.name || details.clientFileName || ''}
            </Descriptions.Item>
            <Descriptions.Item label='Format'>
              {details.fieldDelimiter === FieldDelimiter.FIXED ? 'TXT' : 'CSV'}
            </Descriptions.Item>
            <Descriptions.Item label='Delimiter'>
              {details.fieldDelimiter}
            </Descriptions.Item>
          </Descriptions>
        </GridItem>
        <GridItem>
          <ErrorsAndWarningReporter {...errorsAndIsSSNMapped} />
        </GridItem>
      </Grid>
      <br />
      <HStack>
        <InputFieldMapper
          productName={productName}
          isRemoteFile={props.isRemoteFile}
          setIsRemoteFile={props.setIsRemoteFile}
          tmsNumber={props.tmsNumber}
          subCode={props.subCode}
          inputLabelsMap={inputLabelsMap}
          inputFileDetails={props.details}
          onMapperDisplayed={props.onAfterViewDisplayed}
          previousFieldMappings={props.previousFieldMappings}
          onSuccess={onFileUploadSuccess}
          onNewFileSelected={useCallback(
            (details, mappings) => {
              setIsLoading(true);
              setTimeout(() => {
                onNewFileSelected(details, mappings);
              });
            },
            [onNewFileSelected],
          )}
          onFieldErrorsOrWarnings={useCallback(
            (errors, fieldErrors, isSSNMapped) => {
              setErrorsAndIsSSNMapped({
                errors,
                isSSNMapped,
                fieldErrors,
              });
            },
            [],
          )}
          inputFileSelectionOptions={inputFileSelectionOptions}
          oneClickRemoteFileAccess={props.oneClickRemoteFileAccess}
        />
      </HStack>
    </Spin>
  );
};
